import type { MeasureDescriptor, MeasureSubgroup } from "../../../../../../../../../api/types/svTypes.ts";

export const MAX_SUBGROUP_NAME_LENGTH = 100;

const normalizeSubgroupName = (name: string): string => name.trim().toLowerCase();

export const compareSubgroupsByName = (a: MeasureSubgroup, b: MeasureSubgroup): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

export const getGroupSubgroups = (subgroups: MeasureSubgroup[], group: string | undefined): MeasureSubgroup[] =>
  subgroups.filter((subgroup) => subgroup.group === group).sort(compareSubgroupsByName);

export const validateSubgroupName = (name: string, existingOfGroup: MeasureSubgroup[]): string | undefined => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return "Name is required.";
  }
  if (trimmedName.length > MAX_SUBGROUP_NAME_LENGTH) {
    return `Name cannot be longer than ${MAX_SUBGROUP_NAME_LENGTH} characters.`;
  }
  const normalizedName = normalizeSubgroupName(trimmedName);
  const duplicate = existingOfGroup.find((subgroup) => normalizeSubgroupName(subgroup.name) === normalizedName);
  if (duplicate) {
    return `A subgroup named "${trimmedName}" already exists in ${duplicate.group}.`;
  }
  return undefined;
};

export const validateSubgroupRename = (
  name: string,
  subgroup: MeasureSubgroup,
  existingOfGroup: MeasureSubgroup[]
): string | undefined =>
  validateSubgroupName(
    name,
    existingOfGroup.filter((existing) => existing.id !== subgroup.id)
  );

export const countMeasuresInSubgroup = (
  measures: Pick<MeasureDescriptor, "group" | "subgroupId">[],
  subgroup: MeasureSubgroup
): number =>
  measures.filter((measure) => measure.subgroupId === subgroup.id && measure.group === subgroup.group).length;

export const resolveSubgroupId = (
  subgroupId: string | undefined,
  subgroupsOfGroup: MeasureSubgroup[]
): string | undefined => subgroupsOfGroup.find((subgroup) => subgroup.id === subgroupId)?.id;

export const resolveMeasureSubgroupId = (
  measure: Pick<MeasureDescriptor, "group" | "subgroupId">,
  subgroups: MeasureSubgroup[]
): string | undefined => resolveSubgroupId(measure.subgroupId, getGroupSubgroups(subgroups, measure.group));

export interface SubgroupMeasureOption {
  measureId: string;
  caption: string;
  subgroupId?: string;
  isLocked: boolean;
}

export const getSubgroupMeasureOptions = (
  measures: Pick<MeasureDescriptor, "measureId" | "caption" | "custom" | "group" | "subgroupId" | "isLocked">[],
  group: string
): SubgroupMeasureOption[] =>
  measures
    .flatMap(({ measureId, caption, custom, group: measureGroup, subgroupId, isLocked }) =>
      custom && measureGroup === group && measureId !== undefined
        ? [{ measureId, caption, subgroupId, isLocked: isLocked === true }]
        : []
    )
    .sort((a, b) => a.caption.localeCompare(b.caption, undefined, { sensitivity: "base" }));

export const getSubgroupMemberIds = (
  measures: Pick<MeasureDescriptor, "measureId" | "subgroupId">[],
  subgroupId: string
): string[] =>
  measures.flatMap(({ measureId, subgroupId: measureSubgroupId }) =>
    measureSubgroupId === subgroupId && measureId !== undefined ? [measureId] : []
  );

export type SelectAllState = "all" | "some" | "none";

const getSelectableMeasureIds = (options: SubgroupMeasureOption[]): string[] =>
  options.filter((option) => !option.isLocked).map((option) => option.measureId);

export const getSelectAllState = (options: SubgroupMeasureOption[], selectedMeasureIds: string[]): SelectAllState => {
  const selectableIds = getSelectableMeasureIds(options);
  const selectedCount = selectableIds.filter((measureId) => selectedMeasureIds.includes(measureId)).length;
  if (selectableIds.length > 0 && selectedCount === selectableIds.length) {
    return "all";
  }
  return selectedCount > 0 ? "some" : "none";
};

export const toggleAllSubgroupMeasures = (options: SubgroupMeasureOption[], selectedMeasureIds: string[]): string[] => {
  const selectableIds = getSelectableMeasureIds(options);
  if (getSelectAllState(options, selectedMeasureIds) === "all") {
    return selectedMeasureIds.filter((measureId) => !selectableIds.includes(measureId));
  }
  return [...selectedMeasureIds, ...selectableIds.filter((measureId) => !selectedMeasureIds.includes(measureId))];
};
